import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../../../server";
import { uploadCatalogImage, fetchSiteOptions } from "../../../redux/actions/siteOptions";
import { useDispatch } from "react-redux";
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from "../../../uploadLimits";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import MaintenancePage from "../../../pages/MaintenancePage";

const defaultSettings = {
  enabled: false,
  title: "We'll be back soon",
  message:
    "The website is currently undergoing maintenance. Please check back shortly.",
  image: "",
};

const MaintenanceSettings = () => {
  const dispatch = useDispatch();
  const [settings, setSettings] = useState(defaultSettings);
  const [headerLogo, setHeaderLogo] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const { data } = await axios.get(`${server}/options/global`, {
          withCredentials: true,
        });
        const m = data?.options?.maintenance || {};
        setHeaderLogo(data?.options?.header?.logo || "");
        setSettings({
          enabled: Boolean(m.enabled),
          title: m.title || defaultSettings.title,
          message: m.message || defaultSettings.message,
          image: m.image || "",
        });
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load settings");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async (nextSettings = settings) => {
    try {
      setIsSaving(true);
      await axios.put(
        `${server}/options/global`,
        { maintenance: nextSettings },
        { withCredentials: true }
      );
      setSettings(nextSettings);
      dispatch(fetchSiteOptions("global"));
      toast.success(
        nextSettings.enabled
          ? "Maintenance mode is on. Visitors will see the maintenance page."
          : "The website is live."
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
      toast.error(`Image exceeds the ${IMAGE_UPLOAD_MAX_MB}MB size limit.`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const data = await dispatch(uploadCatalogImage(formData));
      const filename = data?.filename || "";
      if (filename) {
        setSettings((prev) => ({ ...prev, image: filename }));
        toast.success("Image uploaded. Save to apply it.");
      }
    } catch (err) {
      toast.error(err?.message || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (isLoading) {
    return <div className="p-6 text-slate-500">Loading maintenance settings…</div>;
  }

  const imagePreview = resolveMediaUrl(settings.image);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Maintenance mode</h2>
        <p className="mt-1 text-sm text-slate-500">
          When maintenance is on, visitors see the page below. Admins can still use the full website and this dashboard.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <p className="mb-3 text-sm font-medium text-slate-700">Website status</p>
        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1">
          <button
            type="button"
            disabled={isSaving}
            onClick={() => handleSave({ ...settings, enabled: false })}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              !settings.enabled
                ? "bg-[#38513b] text-white shadow"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Live
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={() => handleSave({ ...settings, enabled: true })}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              settings.enabled
                ? "bg-amber-500 text-white shadow"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Maintenance
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          {settings.enabled
            ? "Visitors currently see the maintenance page."
            : "The public website is currently live."}
        </p>
      </div>

      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Heading</span>
          <input
            type="text"
            value={settings.title}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, title: e.target.value }))
            }
            className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-[#38513b]"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Message</span>
          <textarea
            rows={5}
            value={settings.message}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, message: e.target.value }))
            }
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#38513b]"
          />
        </label>

        <div>
          <span className="mb-1 block text-sm font-medium text-slate-700">Image</span>
          <p className="mb-2 text-xs text-slate-500">
            Optional. Maximum upload size: {IMAGE_UPLOAD_MAX_MB}MB.
          </p>
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="Maintenance"
              className="mb-3 max-h-48 rounded-xl border border-slate-200 object-contain"
            />
          ) : null}
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex h-10 cursor-pointer items-center rounded-xl bg-[#38513b] px-4 text-sm font-semibold text-white hover:bg-[#2f4232]">
              {uploading ? "Uploading…" : settings.image ? "Replace image" : "Upload image"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploading}
                onChange={(e) => {
                  handleImageUpload(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
            {settings.image ? (
              <button
                type="button"
                onClick={() => setSettings((prev) => ({ ...prev, image: "" }))}
                className="inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Remove image
              </button>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          disabled={isSaving}
          onClick={() => handleSave(settings)}
          className="inline-flex h-11 items-center rounded-xl bg-[#38513b] px-6 text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-60"
        >
          {isSaving ? "Saving…" : "Save content"}
        </button>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-slate-800">Visitor preview</h3>
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <div className="max-h-[420px] overflow-y-auto">
            <MaintenancePage settings={settings} headerLogo={headerLogo} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceSettings;
